import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { HandoverChain, HandoverStage } from '../../../models/handover-chain.model';
import { TestCaseHandoverService } from '../../../services/test-case-handover.service';
import { HeaderInboxBadgeService } from '../../../services/header-inbox-badge.service';

@Component({
  selector: 'app-test-case-handover-chain',
  imports: [ReactiveFormsModule, TranslatePipe, MatIcon, AvatarComponent, PersonSelectComponent],
  templateUrl: './test-case-handover-chain.component.html',
  styleUrl: './test-case-handover-chain.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseHandoverChainComponent {
  private handoverService = inject(TestCaseHandoverService);
  private inboxBadge = inject(HeaderInboxBadgeService);
  private destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);
  readonly readonly = input<boolean>(false);
  readonly personOptions = input<PersonOption[]>([]);
  readonly currentUserId = input<string | null>(null);

  readonly changed = output<void>();
  readonly handoverDraftChange = output<string | null>();

  readonly chain = signal<HandoverChain | null>(null);
  readonly handoffPanelOpen = signal(false);
  readonly selectedUserId = new FormControl<string>('', { nonNullable: true });
  readonly pendingUserId = signal<string | null>(null);

  readonly stages = computed(() => this.chain()?.stages ?? []);
  readonly closed = computed(() => this.chain()?.closed ?? false);
  readonly activeStageId = computed(() => {
    if (this.closed()) return null;
    const stages = this.stages();
    const last = stages.length > 0 ? stages[stages.length - 1] : null;
    return last && !last.handedOffAt ? last.id : null;
  });

  readonly pendingStage = computed<HandoverStage | null>(() => {
    const userId = this.pendingUserId();
    if (!userId) return null;
    const option = this.personOptions().find((o) => o.id === userId);
    return {
      id: 'draft-handover',
      orderIndex: this.stages().length,
      userId,
      displayName: option?.displayName ?? null,
      handedOffAt: null,
      createdAt: '',
    };
  });

  readonly currentOwnerAlreadyHandedOff = computed(() => {
    const stages = this.stages();
    if (stages.length === 0) return false;
    const last = stages[stages.length - 1];
    if (last.handedOffAt !== null) return false;
    return stages.some((s) => s !== last && s.handedOffAt !== null && s.userId === last.userId);
  });

  readonly currentOwnerUserId = computed<string | null>(() => {
    const stages = this.stages();
    const last = stages.length > 0 ? stages[stages.length - 1] : null;
    return last && last.handedOffAt === null ? last.userId : null;
  });

  readonly selectablePersonOptions = computed<PersonOption[]>(() => {
    const currentOwner = this.currentOwnerUserId();
    const me = this.currentUserId();
    return this.personOptions().filter((o) => o.id !== currentOwner && o.id !== me);
  });

  constructor() {
    effect(() => {
      const id = this.testCaseId();
      if (id) this.load(id);
      else this.chain.set(null);
    });
  }

  private load(testCaseId: string): void {
    this.handoverService
      .list(this.projectId(), testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (chain) => this.chain.set(chain),
        error: () => this.chain.set({ stages: [], closed: false, closedAt: null, closedBy: null }),
      });
  }

  onOpenHandoff(): void {
    if (this.readonly() || this.currentOwnerAlreadyHandedOff()) return;
    this.selectedUserId.reset('');
    this.handoffPanelOpen.set(true);
  }

  onCloseHandoffPanel(): void {
    this.handoffPanelOpen.set(false);
    this.selectedUserId.reset('');
  }

  onSubmitHandoff(): void {
    const userId = this.selectedUserId.value;
    if (!userId || this.currentOwnerAlreadyHandedOff()) return;
    this.pendingUserId.set(userId);
    this.handoffPanelOpen.set(false);
    this.selectedUserId.reset('');
    this.handoverDraftChange.emit(userId);
  }

  onDiscardDraft(): void {
    if (this.pendingUserId() === null) return;
    this.pendingUserId.set(null);
    this.handoverDraftChange.emit(null);
  }

  resetPending(): void {
    if (this.pendingUserId() === null) return;
    this.pendingUserId.set(null);
  }

  reloadChain(): void {
    const testCaseId = this.testCaseId();
    if (testCaseId) this.load(testCaseId);
  }

  refreshInboxBadge(): void {
    this.inboxBadge.refresh();
  }

  onCloseChain(): void {
    const testCaseId = this.testCaseId();
    if (!testCaseId || this.readonly()) return;
    this.handoverService
      .close(this.projectId(), testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (chain) => {
          this.chain.set(chain);
          this.changed.emit();
        },
      });
  }

  onReopenChain(): void {
    const testCaseId = this.testCaseId();
    if (!testCaseId || this.readonly()) return;
    this.handoverService
      .reopen(this.projectId(), testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (chain) => {
          this.chain.set(chain);
          this.changed.emit();
        },
      });
  }

  firstName(stage: HandoverStage): string {
    const name = stage.displayName ?? '';
    return name.split(/\s+/)[0] ?? '';
  }

  fullName(stage: HandoverStage): string {
    return stage.displayName ?? '';
  }

  isActive(stage: HandoverStage): boolean {
    return this.activeStageId() === stage.id;
  }
}
